import { RouteComponentProps } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { API } from 'api';
import { getPageStatus, PageLayout } from 'components/PageLayout';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { useAuthState } from 'state/useAuthState';
import { AxiosResponse } from 'axios';
import { parseGeoEventDates } from 'features/geoEvents/geoEvent.utils';
import { WithStringDates } from 'shared/types';
import { EditAutomatedGeoEventForm } from './EditAutomatedGeoEventForm';

type ContentProps = {
  geoEvent: Wildfire;
};

const fetchGeoEvent = async (
  geoEventId: string,
): Promise<AxiosResponse<Wildfire>> => {
  const res = await API.get<WithStringDates<Wildfire>>(
    `geo_events/${geoEventId}`,
  );
  return { ...res, data: parseGeoEventDates(res.data) };
};

const Content = (props: ContentProps): JSX.Element => {
  const { geoEvent } = props;
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t('editGeoEvent.title')}</title>
      </Helmet>
      <EditAutomatedGeoEventForm geoEvent={geoEvent} />
    </>
  );
};

export const EditAutomatedGeoEvent = (
  props: RouteComponentProps<{ geoEventId: string }>,
): JSX.Element => {
  const {
    match: {
      params: { geoEventId },
    },
  } = props;
  const { t } = useTranslation();
  const {
    permissions: { canReportWildfire },
  } = useAuthState();

  const query = useQuery({
    queryKey: ['geoEvent', geoEventId],
    queryFn: () => fetchGeoEvent(geoEventId),
    enabled: Boolean(geoEventId) && canReportWildfire,
  });
  const { data, error, isLoading, isError } = query;

  const canReport =
    canReportWildfire && data?.data?.geoEventType === 'wildfire';

  const status = getPageStatus({
    isUnauthorized: !isLoading && !canReport,
    isLoading,
    isError: isError || !data?.data,
  });

  return (
    <PageLayout
      data-cname="EditGeoEventPage"
      status={status}
      headerBarProps={{
        title: data?.data?.name,
        action: 'back',
      }}
      errorMessage={
        error ? JSON.stringify(error) : t('editGeoEvent.errors.notFound')
      }
    >
      {data && <Content geoEvent={data.data} />}
    </PageLayout>
  );
};
